import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { ArrowUpRight, Check, Mail, MapPin } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COMPANY } from "@/data/site";

const CLIENT_TYPES = ["Broker Dealer", "Hedge Fund", "Proprietary Trading Firm", "Family Office", "Other"];

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [wholesale, setWholesale] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", clientType: "", message: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error("Please complete the required fields.");
      return;
    }
    if (!wholesale) {
      toast.error("Please confirm you are enquiring as a wholesale client.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, wholesaleConfirmed: wholesale });
      setSubmitted(true);
      toast.success("Thank you — a specialist will be in touch.");
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        overline="Contact"
        title="Start a conversation."
        subtitle="Every ET Prime relationship begins with a discussion about your book, your markets and the counterparty terms that would move your business forward. Reach our institutional desk below."
      />

      <section className="py-24 md:py-32 relative z-10" data-testid="contact-body">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* FORM */}
          <div className="lg:col-span-7">
            {submitted ? (
              <Reveal>
                <div className="border border-[#C8A45A]/40 bg-[#11151C]/60 p-12 text-center" data-testid="contact-success">
                  <div className="w-16 h-16 mx-auto border border-[#C8A45A] flex items-center justify-center">
                    <Check className="gold-text" size={28} />
                  </div>
                  <h3 className="font-display text-2xl text-white mt-8">Message received.</h3>
                  <p className="text-[#A1A5AB] mt-4 max-w-md mx-auto leading-relaxed">
                    Thank you for reaching out to {COMPANY.shortName}. A member of our institutional desk will
                    respond to your enquiry shortly.
                  </p>
                </div>
              </Reveal>
            ) : (
              <Reveal>
                <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-xs uppercase tracking-widest text-[#737882]">Full name *</Label>
                      <Input
                        id="name" value={form.name} onChange={update("name")}
                        data-testid="contact-name"
                        className="bg-transparent border-white/15 rounded-none h-12 text-white focus-visible:ring-0 focus-visible:border-[#C8A45A]"
                        placeholder="Your name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-xs uppercase tracking-widest text-[#737882]">Work email *</Label>
                      <Input
                        id="email" type="email" value={form.email} onChange={update("email")}
                        data-testid="contact-email"
                        className="bg-transparent border-white/15 rounded-none h-12 text-white focus-visible:ring-0 focus-visible:border-[#C8A45A]"
                        placeholder="you@firm.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-xs uppercase tracking-widest text-[#737882]">Firm *</Label>
                      <Input
                        id="company" value={form.company} onChange={update("company")}
                        data-testid="contact-company"
                        className="bg-transparent border-white/15 rounded-none h-12 text-white focus-visible:ring-0 focus-visible:border-[#C8A45A]"
                        placeholder="Company name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-widest text-[#737882]">Client type</Label>
                      <Select value={form.clientType} onValueChange={(v) => setForm((f) => ({ ...f, clientType: v }))}>
                        <SelectTrigger
                          data-testid="contact-client-type"
                          className="bg-transparent border-white/15 rounded-none h-12 text-white focus:ring-0 focus:border-[#C8A45A]"
                        >
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#11151C] border-white/15 text-white">
                          {CLIENT_TYPES.map((t) => (
                            <SelectItem key={t} value={t} data-testid={`client-type-${t.toLowerCase().replace(/\s+/g, "-")}`}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-xs uppercase tracking-widest text-[#737882]">How can we help?</Label>
                    <Textarea
                      id="message" value={form.message} onChange={update("message")}
                      data-testid="contact-message"
                      rows={5}
                      className="bg-transparent border-white/15 rounded-none text-white focus-visible:ring-0 focus-visible:border-[#C8A45A] resize-none"
                      placeholder="Tell us about your desk, the markets you trade and what you're looking for in a counterparty."
                    />
                  </div>

                  <div className="flex items-start gap-3 border border-white/10 bg-[#11151C]/40 p-5" data-testid="contact-wholesale-block">
                    <Checkbox
                      id="wholesale"
                      checked={wholesale}
                      onCheckedChange={(v) => setWholesale(v === true)}
                      data-testid="contact-wholesale-checkbox"
                      className="mt-0.5 border-white/30 data-[state=checked]:bg-[#C8A45A] data-[state=checked]:border-[#C8A45A] data-[state=checked]:text-[#0A0D12]"
                    />
                    <Label htmlFor="wholesale" className="text-xs text-[#A1A5AB] leading-relaxed cursor-pointer font-normal">
                      By submitting this form I confirm that I am making an enquiry on behalf of a wholesale client
                      (or a person who expects to qualify as a wholesale client), that I am not seeking personal
                      advice, and that I have read the{" "}
                      <Link to="/legal/wholesale-client-notice" className="text-[#C8A45A] underline" data-testid="contact-wholesale-link">Wholesale Client Notice</Link>{" "}
                      and{" "}
                      <Link to="/legal/privacy" className="text-[#C8A45A] underline">Privacy Policy</Link>. Submitting
                      this form does not create a client relationship.
                    </Label>
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={sending}
                    className="group inline-flex items-center gap-2 bg-[#C8A45A] text-[#0A0D12] hover:bg-[#D4B572] transition-colors px-9 py-4 text-sm font-medium tracking-wide disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {sending ? "Sending…" : "Send enquiry"}
                    <ArrowUpRight size={17} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>

                  <p className="text-xs text-[#737882] font-mono pt-2">{COMPANY.clientNotice}</p>
                </form>
              </Reveal>
            )}
          </div>

          {/* DETAILS */}
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="border border-white/10 bg-[#11151C]/40 p-10 space-y-10">
                <div className="flex gap-4">
                  <Mail className="gold-text shrink-0 mt-1" size={20} />
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#737882]">Institutional desk</div>
                    <a href={`mailto:${COMPANY.email}`} className="text-white hover:text-[#C8A45A] transition-colors block mt-2" data-testid="contact-detail-email">
                      {COMPANY.email}
                    </a>
                  </div>
                </div>
                <div className="flex gap-4">
                  <MapPin className="gold-text shrink-0 mt-1" size={20} />
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#737882]">Registered office</div>
                    <address className="not-italic text-[#A1A5AB] mt-2 leading-relaxed" data-testid="contact-detail-address">
                      {COMPANY.addressLines.map((l) => (
                        <span key={l}>{l}<br /></span>
                      ))}
                    </address>
                  </div>
                </div>
                <div className="border-t border-white/10 pt-8">
                  <div className="text-xs uppercase tracking-widest text-[#737882]">Entity</div>
                  <div className="text-[#A1A5AB] mt-2 text-sm leading-relaxed">
                    {COMPANY.legalName}<br />
                    ACN {COMPANY.acn}<br />
                    ABN {COMPANY.abn}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
